import React from 'react';
import { Loader2, Send } from 'lucide-react';
import { getBackendUrl } from '../../utils/apiUrl';

const sessionToken = () => {
  try { return JSON.parse(sessionStorage.getItem('customerSession') || '{}').token || ''; }
  catch { return ''; }
};

// Both portal chat surfaces use the same authenticated support conversation.
export default function CustomerHandoffChat({ ticketId, onClosed }) {
  const [conversation, setConversation] = React.useState(null);
  const [input, setInput] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loadError, setLoadError] = React.useState('');
  const pending = React.useRef(null);
  const bottom = React.useRef(null);
  const url = `${getBackendUrl()}/api/customer/opsy/handoff/${encodeURIComponent(ticketId)}?session_token=${encodeURIComponent(sessionToken())}`;

  React.useEffect(() => {
    let stopped = false;
    let timer;
    const controller = new AbortController();
    const refresh = async () => {
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Unable to load the team conversation. Please try again.');
        const data = await response.json();
        if (!stopped) { setConversation(data); setLoadError(''); }
      } catch (err) {
        if (!stopped) setLoadError(err.message);
      } finally {
        if (!stopped) timer = setTimeout(refresh, 4000);
      }
    };
    refresh();
    return () => { stopped = true; controller.abort(); clearTimeout(timer); };
  }, [url]);

  React.useEffect(() => { bottom.current?.scrollIntoView({ block: 'nearest' }); }, [conversation?.messages?.length]);
  const closed = conversation && !['open', 'pending'].includes(conversation.status);
  const send = async () => {
    const content = input.trim();
    if (!conversation || !content || sending || closed) return;
    // Retrying an uncertain network result must not create duplicate tickets/messages.
    if (!pending.current || pending.current.content !== content) {
      pending.current = { content, request_id: crypto.randomUUID() };
    }
    setSending(true);
    setError('');
    try {
      const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pending.current) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Unable to confirm delivery. Please retry.');
      setInput('');
      pending.current = null;
      // The normal polling loop also recovers if this refresh fails after a successful send.
      try {
        const latest = await fetch(url);
        if (!latest.ok) throw new Error('Refresh failed');
        setConversation(await latest.json());
        setLoadError('');
      } catch { setLoadError('Your message was sent. Reconnecting to load team replies…'); }
    } catch (err) { setError(err.message || 'Unable to confirm delivery. Please retry.'); }
    finally { setSending(false); }
  };

  return (
    <section data-testid="customer-handoff-chat" className="flex flex-col flex-1 min-h-0 bg-white text-zinc-800">
      <div className="px-4 py-3 border-b border-zinc-200">
        <h3 className="font-semibold">Conversation with our team</h3>
        <p className="text-sm text-zinc-500">{closed ? 'This support conversation is closed.' : 'Your recent conversation has been shared. Add details here; team replies will appear below.'}</p>
      </div>
      <div className="flex-1 min-h-32 max-h-96 overflow-y-auto p-3 space-y-3" aria-live="polite">
        {!conversation && <Loader2 className="h-5 w-5 animate-spin" aria-label="Loading conversation" />}
        {(conversation?.messages || []).map(message => (
          <div key={message.id} className={`rounded-xl p-3 text-sm break-words ${message.sender_type === 'agent' ? 'bg-emerald-50 border border-emerald-100' : 'bg-zinc-50'}`}>
            <p className="font-semibold text-xs mb-1">{message.sender_type === 'agent' ? (message.sender_name || 'Support team') : 'You'}</p>
            {message.content?.startsWith('Customer asked to talk to a human via ') ? (
              <details><summary className="cursor-pointer">Conversation shared with the team</summary><p className="whitespace-pre-wrap mt-2">{message.content}</p></details>
            ) : <p className="whitespace-pre-wrap">{message.content}</p>}
            {message.image_url && <img src={message.image_url} alt="Support attachment" className="max-w-full rounded-lg mt-2" />}
          </div>
        ))}
        <div ref={bottom} />
      </div>
      {(error || loadError) && <p role="alert" className="px-3 py-2 text-sm text-red-700">{error || loadError}</p>}
      {closed ? <button type="button" onClick={onClosed} className="m-3 rounded-lg bg-emerald-600 px-3 py-2 text-white">Back to OpSy</button> : (
        <div className="flex gap-2 p-3 border-t border-zinc-200">
          <textarea aria-label="Message our team" value={input} maxLength={5000} onChange={event => setInput(event.target.value)}
            onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); } }}
            placeholder="Add a message for our team…" rows={2} className="min-w-0 flex-1 resize-none rounded-lg border p-2 text-sm" />
          <button type="button" aria-label="Send to team" onClick={send} disabled={!conversation || sending || !input.trim()}
            className="self-end rounded-lg bg-emerald-600 text-white p-3 disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      )}
    </section>
  );
}
